import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import api, { money } from "@/lib/api";
import PortalLayout from "@/components/portal/PortalLayout";
import StatusBadge, { ORDER_FLOW } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const reload = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  useEffect(() => { reload(); api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {}); }, [id]);

  const uploadReceipt = async (e) => {
    const f = e.target.files?.[0]; if (e.target) e.target.value = ""; if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) { toast.error("Only JPG or PNG allowed"); return; }
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/uploads?private=false", fd);
      await api.post(`/orders/${id}/receipt`, { receipt_id: up.data.id });
      toast.success("Receipt submitted — our team will verify it shortly.");
      reload();
    } catch (err) { toast.error("Upload failed. Please try again."); }
  };

  if (!order) return <PortalLayout title="Order"><p className="text-muted-foreground">Loading...</p></PortalLayout>;

  const currentIdx = ORDER_FLOW.indexOf(order.status);
  const paid = (order.schedule || []).filter((x) => x.paid).length;
  const pct = order.schedule?.length ? Math.round((paid / order.schedule.length) * 100) : 0;
  const trackMap = {};
  (order.tracking || []).forEach((t) => { trackMap[t.status] = t.at; });

  return (
    <PortalLayout>
      <Link to="/portal/orders" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" />Back to Orders</Link>
      <div className="flex flex-wrap items-center gap-3">
        <img src={order.product_image} alt={order.product_name} className="h-16 w-16 rounded-lg object-cover" />
        <div><h1 className="font-display text-2xl font-bold">{order.product_name}</h1><p className="text-sm text-muted-foreground">{order.order_no} · {order.plan_name}</p></div>
        <div className="ml-auto"><StatusBadge status={order.status} /></div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 grid gap-6">
          {order.status === "Pending Down Payment" && (
            <div className="rounded-xl border-2 border-amber-500/40 bg-card p-6" data-testid="downpayment-card">
              <h3 className="font-display font-semibold text-amber-600 dark:text-amber-400">Pending Down Payment</h3>
              <p className="mt-1 text-sm text-muted-foreground">Pay the advance of <span className="font-semibold text-foreground">{money(order.advance_payment)}</span> using any option below, then upload your receipt. Your order proceeds once our team verifies it.</p>
              <div className="mt-4 flex flex-wrap gap-4">
                {(settings.payment_methods || []).filter((p) => p.enabled).map((p) => (
                  <div key={p.key} className="w-36 rounded-lg border border-border p-3 text-center" data-testid={`pay-option-${p.key}`}>
                    {p.qr ? <img src={p.qr} alt={p.name} className="mx-auto h-28 w-28 object-contain" /> : p.logo ? <img src={p.logo} alt={p.name} className="mx-auto h-12 object-contain" /> : null}
                    <p className="mt-2 text-xs font-semibold">{p.name}</p>
                    {p.instructions && <p className="text-[10px] text-muted-foreground">{p.instructions}</p>}
                  </div>
                ))}
              </div>
              <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground" data-testid="upload-receipt-btn">
                Upload Payment Receipt
                <input type="file" accept=".jpg,.jpeg,.png" className="hidden" onChange={uploadReceipt} />
              </label>
              {(order.receipts || []).length > 0 && <p className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">{order.receipts.length} receipt(s) submitted — awaiting verification.</p>}
            </div>
          )}
          {/* Tracking timeline */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-display font-semibold">Order Tracking</h3>
            <div className="grid gap-1">
              {ORDER_FLOW.map((step, i) => {
                const done = i <= currentIdx;
                return (
                  <div key={step} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      {done ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5 text-muted-foreground/40" />}
                      {i < ORDER_FLOW.length - 1 && <div className={`h-6 w-0.5 ${i < currentIdx ? "bg-primary" : "bg-border"}`} />}
                    </div>
                    <div className="pb-1"><p className={`text-sm font-medium ${done ? "" : "text-muted-foreground"}`}>{step}</p>{trackMap[step] && <p className="text-xs text-muted-foreground">{new Date(trackMap[step]).toLocaleString()}</p>}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Schedule */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-display font-semibold">Installment Schedule</h3><span className="font-display font-extrabold text-primary">{paid}/{order.schedule?.length} Paid</span></div>
            <Progress value={pct} className="mb-4 h-2.5" />
            <div className="grid gap-2">
              {(order.schedule || []).map((s) => (
                <div key={s.index} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm">
                  <span className="font-medium">#{s.index}</span>
                  <span className="text-muted-foreground">Due {s.due_date}</span>
                  <span className="font-semibold">{money(s.amount)}</span>
                  {s.paid ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">Paid</span> : <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400">Pending</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-3 font-display font-semibold">Summary</h3>
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Order Date</span><span data-testid="order-date">{order.created_at ? new Date(order.created_at).toLocaleString() : "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Payable</span><span className="font-semibold">{money(order.total_payable)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Advance</span><span>{money(order.advance_payment)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Monthly</span><span className="font-semibold text-primary">{money(order.monthly_installment)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Duration</span><span>{order.duration_months} months</span></div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-3 font-display font-semibold">Payment History</h3>
            {(order.payments || []).length === 0 ? <p className="text-sm text-muted-foreground">No payments recorded yet.</p> : (
              <div className="grid gap-2 text-sm">
                {order.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2">
                    <div><p className="font-medium capitalize">{p.type} {p.installment_index > 0 ? `#${p.installment_index}` : ""}</p><p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()} · {p.method}</p></div>
                    <span className="font-semibold text-primary">{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
